import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  MDBDatatable,
  MDBFooter,
  MDBBadge,
  MDBRow,
  MDBCol,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
  MDBIcon,
  MDBCheckbox,
} from "mdb-react-ui-kit";
import Pagination from "./Pagination";
import Header from "./Header";
import { useCookies } from "react-cookie";
import { debounce } from "lodash";

const archiveTableHeading = [
  { label: "Bill #", field: "Bill #", sort: false, width: "auto" },
  { label: "LC #", field: "LC #", sort: false, width: "auto" },
  { label: "Short Title", field: "Short Title", sort: false, width: 400 },
  {
    label: "Primary Sponsor",
    field: "Primary Sponsor",
    sort: false,
    width: "auto",
  },
  { label: "Status Date", field: "Status Date", sort: false, width: "auto" },
  { label: "Status", field: "Status", sort: false, width: "auto" },
];

const domain = window.location.hostname;
const port = window.location.port;
const protocol = window.location.protocol;

const getFullDomain = () => {
  if (port) return `${domain}:${port}`;
  return domain;
};

const createFullUrl = (path) => {
  return `${protocol}//${getFullDomain()}${path}`;
};

const BillsTable = ({
  sessionId,
  legislatureId,
  subjectCode,
  setSideNavToggle,
  sideNavToggle,
  selectedBillTypes,
  selectedLegislator,
  offset,
  setOffset,
  selectedDrafter,
  selectedParties,
  selectedChambers,
  setChambersCount,
  setBillTypesCount,
  setPartyCount,
  selectedCommittee,
  selectedProgress,
  hasAmendment,
  hasFiscalNotes,
  hasLegalNotes,
  setDealineCategoriesCount,
  selectedDeadlines,
  selectedActionTypes,
  statusStartDate,
  statusEndDate,
  setChaptersCount,
  selectedChapters,
  sessionInfoForHeader,
  hasPreIntroductionRequired,
  requestor,
  requestorType,
  byRequestOf,
  byRequestOfType,
}) => {
  const [cookies, setCookie] = useCookies(["selectedColumns"]);
  const [allRequestorData, setAllRequestorData] = useState([]);
  const [noResultsMessage, setNoResultsMessage] = useState("Loading Bills...");
  const [bills, setBills] = useState({ columns: [], rows: [] });
  const [totalPages, setTotalPages] = useState(0);
  const [lastPage, setLastPage] = useState(false);
  const [totalElements, setTotalElements] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billSearchText, setBillSearchText] = useState("");
  const [exportDataReady, setExportDataReady] = useState(false);
  const [selectedHeaderValue, setSelectedHeaderValue] = useState(["LC #"]);
  const [billTypes, setBillTypes] = useState([]);
  const [abortControllerQueue, setAbortControllerQueue] = useState([]);
  const [selectHeaders, setSelectHeaders] = useState([
    { text: "Bill #", value: "Bill #" },
    { text: "LC #", value: "LC #" },
    { text: "Requester", value: "Requestor" },
    { text: "Primary Sponsor", value: "Primary Sponsor" },
    { text: "By Request Of", value: "By Request Of" },
    { text: "Chapter", value: "Chapter" },
  ]);
  const [tableHeading, setTableHeading] = useState([
    {
      label: "Bill #",
      field: "Bill #",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "LC #",
      field: "LC #",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "Short Title",
      field: "Short Title",
      sort: false,
      width: 400,
      displayHeader: true,
      dynamicHeader: false,
    },
    {
      label: "Requester",
      field: "Requestor",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "By Request Of",
      field: "By Request Of",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "Primary Sponsor",
      field: "Primary Sponsor",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: true,
    },
    {
      label: "Chapter",
      field: "Chapter",
      sort: false,
      width: "auto",
      displayHeader: false,
      dynamicHeader: true,
    },
    {
      label: "Status Date",
      field: "Status Date",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: false,
    },
    {
      label: "Status",
      field: "Status",
      sort: false,
      width: "auto",
      displayHeader: true,
      dynamicHeader: false,
    },
  ]);
  const [dataForExport, setDataForExport] = useState(tableHeading);

  useEffect(() => {
    const fetchAllRequestors = async () => {
      try {
        const requestorApiCalls = [
          axios.post(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=9999&offset=0`,
            { sessionIds: [sessionId] }
          ),
          axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/findBySessionId?sessionId=${sessionId}`
          ),
          axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/nonStandingCommittees/findByLegislatureId?legislatureId=${sessionId}`
          ),
          axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/electedOfficialPositions`
          ),
          axios.get(
            `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/organizations`
          ),
        ];
        const results = await Promise.all(requestorApiCalls);
        const extractedData = results.map((response) => response.data);
        setAllRequestorData(extractedData);
      } catch (error) {
        console.error("Error fetching requestor data:", error);
      }
    };
    fetchAllRequestors();
  }, [sessionId]);

  /*useEffect(() => {
        const savedSelection = cookies.selectedColumns;
        if (savedSelection) {
            try {
                if (Array.isArray(savedSelection)) {
                    setSelectedHeaderValue(savedSelection);
                    setSelectedHeaders(savedSelection);
                } else {
                    const parsedSelection = JSON.parse(savedSelection);
                    setSelectedHeaderValue(parsedSelection);
                    setSelectedHeaders(parsedSelection);
                }
            } catch (error) {
                console.error("Error parsing saved selection:", error);
                // If parsing fails, try to split the string
                if (typeof savedSelection === "string") {
                    const fallbackSelection = savedSelection
                        .split(",")
                        .map((item) => item.trim());
                    setSelectedHeaderValue(fallbackSelection);
                } else {
                    // If all else fails, set to default
                    setSelectedHeaderValue(["LC #"]);
                }
            }
        }
    }, [cookies.selectedColumns]);*/

  const convertISOToReadableTimeFormat = (date) => {
    return format(parseISO(date), "MM/dd/yy");
  };

  const convertToISOString = (dateString) => {
    const [month, day, year] = dateString.split("/");
    const date = new Date(year, month - 1, day);
    return date.toISOString();
  };

  const getPrimarySponsor = (primarySponsorBillRole, billType) => {
    if (primarySponsorBillRole.length === 0) {
      return null;
    }
    const primarySponsor = primarySponsorBillRole[0];
    if (
      primarySponsor &&
      primarySponsor.lawEntity &&
      primarySponsor.legislator &&
      primarySponsor.legislator.politicalParty
    ) {
      return `${primarySponsor.lawEntity.lastName}, ${
        primarySponsor.lawEntity.firstName
      } (${primarySponsor.legislator.politicalParty.partyCode}) ${
        billType ? (billType.startsWith("H") ? "HD" : "SD") : ""
      } ${primarySponsor.legislator.districtNumber}`;
    }
    return null;
  };

  const formatBillStructureForDataTables = (content) => {
    return content.map((item) => {
      return {
        "Bill #":
          item.billNumber && item.billType ? (
            <Link
              to={`/bill/${sessionId}/${item.id.billDraftNumber}?open_tab=sum`}
              className="text-decoration-underline legmt-link"
              target="_blank"
            >
              {item.billType} {item.billNumber}
            </Link>
          ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
          ),
        "LC #": item.id.billDraftNumber ? (
          <Link
            to={`/lc/bill/${sessionId}/${item.id.billDraftNumber}`}
            className="text-decoration-underline legmt-link"
            target="_blank"
          >
            {item.id.billDraftNumber}
          </Link>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "Short Title": item.shortTitle ? (
          <span>{item.shortTitle}</span>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "Primary Sponsor": getPrimarySponsor(
          item.primarySponsorBillRoles,
          item.billType
        ) ? (
          <span>
            {getPrimarySponsor(item.primarySponsorBillRoles, item.billType)}
          </span>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "Status Date": item.lastStatusDate ? (
          <span>
            {convertISOToReadableTimeFormat(item.billActions[0].date)}
          </span>
        ) : (
          <span>
            {convertISOToReadableTimeFormat(item.billActions[0].date)}
          </span>
        ),
        Status: (() => {
          const actionCode = item.billActions[0].actionType.code;
          const isDangerCode =
            actionCode === 275 ||
            actionCode === 274 ||
            actionCode === 40 ||
            actionCode === 675 ||
            actionCode === 674;

          if (isDangerCode) {
            return (
              <MDBBadge className="w-100" color="danger" light>
                {item.billActions[0].actionType.description}
              </MDBBadge>
            );
          } else {
            return (
              <MDBBadge color="custom1" className="badge w-100">
                {item.billActions[0].actionType.description}
              </MDBBadge>
            );
          }
        })(),
      };
    });
  };

  const getFirstNonFiscalResult = (dataArray) => {
    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return null; // Handle empty or invalid input
    }

    for (const item of dataArray) {
      if (
        item.billStatusCode &&
        item.billStatusCode.name &&
        !item.billStatusCode.name.includes("(H) Fiscal") &&
        !item.billStatusCode.name.includes("(S) Fiscal")
      ) {
        return item;
      }
    }

    return dataArray[0];
  };

  const formatLawsBillStructureForDataTables = (content) => {
    return content.map((item) => {
      const sessionId = item.sessionId;

      const latestBillStatusSorted =
        item.draft.billStatuses.length > 0
          ? item.draft.billStatuses.sort(
              (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
            )
          : null;

      const latestBillStatus = getFirstNonFiscalResult(latestBillStatusSorted);

      return {
        "Bill #":
          item.billNumber && item.billType ? (
            <Link
              to={`/laws/bill/${sessionId}/${item.draft.draftNumber}?open_tab=sum`}
              className="text-decoration-underline legmt-link"
              target="_blank"
            >
              {item.billType.code} {item.billNumber}
            </Link>
          ) : (
            <MDBBadge color="secondary" light>
              Not Available
            </MDBBadge>
          ),
        "LC #": item.draft.draftNumber ? (
          <>
            <Link
              to={`/lc/bill/${sessionId}/${item.draft.draftNumber}`}
              className="text-decoration-underline legmt-link"
              target="_blank"
            >
              {item.draft.draftNumber}
            </Link>
            {item.lcTextAvailable ? (
              <Link
                to={`/lc/bill/${sessionId}/${item.draft.draftNumber}?open_tab=lctext`}
                className="text-decoration-underline legmt-link mx-1"
                target="_blank"
              >
                <MDBIcon far icon="file-pdf" />
              </Link>
            ) : (
              <></>
            )}
          </>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "Short Title": item.draft.shortTitle ? (
          <span>{item.draft.shortTitle}</span>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "Primary Sponsor": item.sponsor ? (
          <span>{`${item.sponsor.firstName} ${item.sponsor.lastName}`}</span>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),

        "Status Date": latestBillStatus ? (
          <span>
            {convertISOToReadableTimeFormat(latestBillStatus.timeStamp)}
          </span>
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        Status: latestBillStatus ? (
          (() => {
            const actionCode = parseInt(latestBillStatus.billStatusCode.code);
            const isDangerCode =
              actionCode === 40 ||
              actionCode === 274 ||
              actionCode === 275 ||
              actionCode === 674 ||
              actionCode === 675;

            if (isDangerCode) {
              return (
                <MDBBadge color="danger" className="badge w-100" light>
                  {latestBillStatus.billStatusCode.name}
                </MDBBadge>
              );
            } else {
              return (
                <MDBBadge color="custom1" className="badge w-100">
                  {latestBillStatus.billStatusCode.name}
                </MDBBadge>
              );
            }
          })()
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        Requestor: item.requester ? (
          item.requester
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        "By Request Of": item.byRequestOf ? (
          item.byRequestOf
        ) : (
          <MDBBadge color="secondary" light>
            Not Available
          </MDBBadge>
        ),
        Chapter: "",
      };
    });
  };

  // Send customized json structure for search APIs.
  const filterValidValues = (obj) => {
    const validObj = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== false) {
        if (Array.isArray(value) && value.length > 0) {
          validObj[key] = value;
        } else if (typeof value === "string" && value.trim() !== "") {
          validObj[key] = value;
        } else if (!Array.isArray(value) && typeof value !== "string") {
          validObj[key] = value;
        }
      }
    });
    return validObj;
  };

  const getAllSponsors = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators`
    );
    return data;
  };

  const getSponsorPayload = (text, selectedLegislator) => {
    let sponsorIds = new Set();

    if (selectedLegislator) {
      sponsorIds.add(selectedLegislator);
    }
    return { sponsorIds: Array.from(sponsorIds) };
  };

  const fetchBillTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/billTypes`
      );
      setBillTypes(response.data);
    } catch (error) {
      console.error("Error fetching bill types:", error);
    }
  };

  useEffect(() => {
    fetchBillTypes();
  }, []);

  const getBillTypeId = (code) => {
    const billType = billTypes.find((type) => type.code === code.toUpperCase());
    return billType ? billType.id : null;
  };

  const getSearchPayload = (text) => {
    const searchRequestorsPayloadPrep =
      allRequestorData && allRequestorData[0] && allRequestorData[0].content
        ? allRequestorData[0].content.map((legislator) => ({
            id: legislator.id,
            name: `${legislator.firstName} ${legislator.lastName}`,
          }))
        : [];

    const searchOrgsPayloadPrep =
      allRequestorData && allRequestorData[4] && allRequestorData[4]
        ? allRequestorData[4].map((orgs) => ({
            id: orgs.id,
            name: `${orgs.name}`,
          }))
        : [];

    const billRegex = /^(HB|HR|HJ|SB|SR|SJ)\s*(\d+)$/i;
    const lcRegex = /^LC\s*(\d+)$/i; // New regex for LC format
    const billMatch = text.match(billRegex);
    const lcMatch = text.match(lcRegex);

    let payload = {};

    if (billMatch) {
      const [, billType, billNumber] = billMatch;
      payload = {
        billNumber: Number(billNumber),
        billType: billType.toUpperCase(),
      };
    } else if (lcMatch) {
      const [, lcNumber] = lcMatch;
      payload = {
        draftNumber: `${lcNumber}`,
      };
    } else if (!isNaN(text)) {
      const number = Number(text);
      payload =
        number > 1000
          ? { sessionLawChapterNumber: number }
          : { billNumber: number };
    } else {
      payload = {
        draftNumber: text,
        shortTitle: text,
      };
    }

    const matchedLegislators = searchRequestorsPayloadPrep.filter(
      (legislator) => legislator.name.toLowerCase().includes(text.toLowerCase())
    );

    if (matchedLegislators.length > 0) {
      payload.requesterLegislatorIds = matchedLegislators.map(
        (legislator) => legislator.id
      );
    }

    const matchedOrgs = searchOrgsPayloadPrep.filter((orgs) =>
      orgs.name.toLowerCase().includes(text.toLowerCase())
    );

    if (matchedOrgs.length > 0) {
      payload.requesterOrganizationIds = matchedOrgs.map((orgs) => orgs.id);
    }

    return payload;
  };

  const getAllSessions = async (apiType) => {
    try {
      if (apiType === "LAWS") {
        const { data } = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/sessions`
        );
        return data;
      } else {
        // return response from ARCHIVE
        const { data } = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/archive/v1/sessions`
        );
        return data;
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const searchLawsAndSetBills = useCallback(
    async (resultLimit = 100) => {
      // Abort all previous requests
      setAbortControllerQueue((prevQueue) => {
        debouncedSearchLawsAndSetBills.flush();
        prevQueue.forEach((controller) => controller.abort());
        return [];
      });

      // Create a new AbortController for this request
      const abortController = new AbortController();
      setAbortControllerQueue([abortController]);

      setBills({ columns: tableHeading, rows: [] });
      setLoading(true);

      try {
        const [{ data }, sessions] = await Promise.all([
          fetchBillsData(abortController.signal, resultLimit),
        ]);

        const { content, totalPages, last, totalElements } = data;

        const sponsors = await getAllSponsors();
        const allSessions = await getAllSessions("LAWS");

        // Process content when allRequestorData is ready
        let updatedContent = processContentWhenReady(content, allRequestorData);

        const showPdfLink = content
          .filter((bill) => {
            const hasCode18 = bill.draft.billStatuses.some(
              (status) => status.billStatusCode.code === "18"
            );
            const hasCode100or500 = bill.draft.billStatuses.some(
              (status) =>
                status.billStatusCode.code === "100" ||
                status.billStatusCode.code === "500"
            );
            return hasCode18 && !hasCode100or500;
          })
          .map((bill) => bill.draft.draftNumber);

        updatedContent = content.map((bill) => {
          const sponsor = sponsors.find((s) => s.id === bill.sponsorId);
          const showLcPdf = showPdfLink.includes(bill.draft.draftNumber);
          bill["lcTextAvailable"] = showLcPdf;
          return { ...bill, sponsor: sponsor || null };
        });

        const formattedData = formatBillsData(updatedContent, tableHeading);

        if (formattedData.rows.length === 0) {
          setNoResultsMessage(`No results found`);
        } else {
          setNoResultsMessage(`Loading Bills...`);
        }
        setBills(formattedData);
        setTotalPages(totalPages);
        setTotalElements(totalElements);
        setLastPage(last);
      } catch (error) {
        if (error.name !== "AbortError") {
          handleError(error);
        }
      } finally {
        setLoading(false);
        setAbortControllerQueue((prevQueue) =>
          prevQueue.filter((controller) => controller !== abortController)
        );
      }
    },
    [
      offset,
      legislatureId,
      selectedBillTypes,
      subjectCode,
      statusStartDate,
      statusEndDate,
      selectedChambers,
      selectedCommittee,
      hasAmendment,
      hasFiscalNotes,
      hasLegalNotes,
      selectedProgress,
      selectedLegislator,
      selectedDeadlines,
      selectedDrafter,
      hasPreIntroductionRequired,
      billSearchText,
      requestor,
      requestorType,
      byRequestOf,
      byRequestOfType,
      selectedActionTypes,
      allRequestorData,
    ]
  );

  const fetchBillsData = async (signal, resultLimit) => {
    const url = `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/bills/search?includeCounts=false&sort=billType.sortOrder,desc&sort=billNumber,asc&sort=draft.draftNumber,asc&limit=${resultLimit}&offset=${offset}`;
    const payload = buildSearchPayload();
    return await axios.post(url, payload, { signal });
  };

  const buildSearchPayload = () => {
    const sponsorPayload = getSponsorPayload(
      billSearchText,
      selectedLegislator
    );
    const billTypeRegex = /^(HB|HR|HJ|SB|SR|SJ)\s*\d+$/i;
    const billTypeMatch = billSearchText.match(billTypeRegex);
    return filterValidValues({
      sessionIds: [sessionId],
      billTypeIds: billTypeMatch
        ? [getBillTypeId(billTypeMatch[1])]
        : selectedBillTypes.map((billType) => billType.typeCodeId),
      subjectIds: Array.isArray(subjectCode) ? subjectCode : [subjectCode],
      chambers: selectedChambers.map((chamber) =>
        chamber === "S" ? "SENATE" : chamber === "H" ? "HOUSE" : ""
      ),
      hasAmendments: hasAmendment,
      fiscalNote: hasFiscalNotes,
      legalNote: hasLegalNotes,
      ...(selectedCommittee && { standingCommitteeIds: [selectedCommittee] }),
      parties: Array.isArray(selectedParties)
        ? selectedParties
        : [selectedParties],
      sponsorIds: sponsorPayload.sponsorIds || [],
      billProgressCategoryIds: selectedProgress
        ? Array.isArray(selectedProgress)
          ? selectedProgress
          : [selectedProgress]
        : [],
      deadlineCodeIds: selectedDeadlines,
      drafterStaffMemberIds: selectedDrafter
        ? Array.isArray(selectedDrafter)
          ? selectedDrafter
          : [selectedDrafter]
        : [],
      optionalFields: billSearchText ? getSearchPayload(billSearchText) : false,
      preIntroRequired: hasPreIntroductionRequired,
      requesterFields:
        requestor && requestorType
          ? { id: requestor, type: requestorType }
          : false,
      byRequestOfFields:
        byRequestOf && byRequestOfType
          ? { id: byRequestOf, requestType: byRequestOfType }
          : false,
      billStatusCodeIds: selectedActionTypes,
      latestBillStatusEffectivePeriod:
        (statusStartDate || statusEndDate) && selectedActionTypes
          ? {
              ...(statusStartDate && {
                startDate: convertToISOString(statusStartDate),
              }),
              ...(statusEndDate && {
                endDate: convertToISOString(statusEndDate),
              }),
              billStatusCodeId: selectedActionTypes[0],
            }
          : [],
    });
  };

  const debouncedSearchLawsAndSetBills = useMemo(
    () => debounce(searchLawsAndSetBills, 500),
    [searchLawsAndSetBills]
  );

  useEffect(() => {
    if (sessionId < 1000) {
      if (allRequestorData && allRequestorData.length > 0) {
        debouncedSearchLawsAndSetBills();
      }
    }
    // Clean up the debounced function on unmount
    return () => debouncedSearchLawsAndSetBills.cancel();
  }, [allRequestorData, debouncedSearchLawsAndSetBills, sessionId]);

  const processContentWhenReady = (
    content,
    allRequestorData,
    billsWithLcTextAvailable
  ) => {
    const requestorTypes = {
      LEGISLATOR: allRequestorData[0]?.content,
      STANDING_COMMITTEE: allRequestorData[1],
      NON_STANDING_COMMITTEE: allRequestorData[2],
      ELECTED_OFFICIAL_POSITION: allRequestorData[3]?.content,
      ORGANIZATION: allRequestorData[4],
      AGENCY: allRequestorData[4],
      AGENCY_ON_BEHALF_OF_NON_STANDING_COMMITTEE: allRequestorData[4],
      AGENCY_ON_BEHALF_OF_STANDING_COMMITTEE: allRequestorData[4],
    };

    const processedContent = content
      .map((bill) => processBill(bill, requestorTypes))
      .filter(Boolean);

    return processedContent.map((bill) => ({
      ...bill,
      lcTextAvailable: bill?.draft?.draftNumber
        ? billsWithLcTextAvailable?.find(
            (b) => b.fileName === `${bill.draft.draftNumber}.pdf`
          ) || false
        : false,
    }));
  };

  const processBill = (bill, requestorTypes) => {
    if (!bill || !bill.draft) {
      console.error("Invalid bill object");
      return null;
    }

    const { requesterId, requesterType, byRequestOfs } = bill.draft;

    if (!requesterId || !requesterType) {
      console.error("Missing requester information");
      return null;
    }

    bill.requester = getRequesterName(
      requesterId,
      requesterType,
      requestorTypes
    );

    if (!bill.requester) {
      console.error("Failed to get requester name");
      return null;
    }

    if (Array.isArray(byRequestOfs) && byRequestOfs.length > 0) {
      const { byRequestOfId, billRequestType } = byRequestOfs[0];
      if (byRequestOfId && billRequestType) {
        bill.byRequestOf = getRequesterName(
          byRequestOfId,
          billRequestType,
          requestorTypes
        );
        if (!bill.byRequestOf) {
          console.error("Failed to get byRequestOf name");
          //return null;
        }
      }
    }

    return bill;
  };

  const getRequesterName = (id, type, requestorTypes) => {
    if (
      !id ||
      !type ||
      !(type in requestorTypes) ||
      !Array.isArray(requestorTypes[type])
    ) {
      console.log(`${id}:${type} requestorType not found`);
      return null;
    }

    const requester = requestorTypes[type].find((s) => s && s.id == id);

    if (!requester) {
      return null;
    }

    if (typeof requester === "object") {
      if (requester.lastName && requester.firstName) {
        return `${requester.lastName}, ${requester.firstName}`;
      } else if (requester.name) {
        return requester.name;
      } else if (requester.committeeDetails?.committeeCode?.name) {
        return requester.committeeDetails.committeeCode.name;
      }
    }

    return null;
  };

  const formatBillsData = (updatedContent, tableHeading) => {
    const sortedContent = sortContent(updatedContent, tableHeading);
    const tableRowData = formatLawsBillStructureForDataTables(sortedContent);
    const filteredHeaders = tableHeading.filter(
      (header) => header.displayHeader !== false
    );
    return { columns: filteredHeaders, rows: tableRowData };
  };

  const sortContent = (content, tableHeading) => {
    if (!content || !Array.isArray(content) || content.length === 0) {
      return [];
    }
    return content;
  };

  const handleError = (error) => {
    if (axios.isCancel(error)) {
      //console.log('Request canceled');
    } else {
      console.error("Error fetching data: ", error);
      throw error;
    }
  };

  const searchLawsArchiveAndSetBills = useCallback(
    async (resultLimit = 100) => {
      // Abort all previous requests
      setAbortControllerQueue((prevQueue) => {
        prevQueue.forEach((controller) => controller.abort());
        return [];
      });

      // Create a new AbortController for this request
      const abortController = new AbortController();
      setAbortControllerQueue([abortController]);

      setBills({ columns: tableHeading, rows: [] });
      setLoading(true);

      try {
        const {
          data: {
            bills: { content, totalPages, last },
            counts,
          },
        } = await axios.post(
          `${process.env.REACT_APP_LAWS_ARCHIVE_BACKEND_HOST}/v1/bills/search?limit=${resultLimit}&offset=${offset}`,
          filterValidValues({
            sessionId: sessionId,
            billTypes: selectedBillTypes.map((billType) => billType.typeCode),
            legislator: selectedLegislator,
            subjects: subjectCode,
            drafter: selectedDrafter,
            parties: selectedParties,
            chamber:
              selectedChambers.includes("S") && selectedChambers.includes("H")
                ? ""
                : selectedChambers[0],
            committeeCode: selectedCommittee,
            currentProgressCategory:
              selectedProgress && selectedProgress !== 0
                ? String(selectedProgress)
                : undefined,
            currentStatus: selectedActionTypes,
            hasAmendment: hasAmendment,
            hasFiscalNote: hasFiscalNotes,
            hasLegalNote: hasLegalNotes,
            deadlineCategories: selectedDeadlines,
            text: billSearchText,
            sortBy: "billNumber",
            dateFrom: statusStartDate,
            dateTo: statusEndDate,
            sessionLawChapterNumbers: selectedChapters,
          }),
          { signal: abortController.signal }
        );

        // COUNTS FOR ADVANCED FILTER
        counts.hasOwnProperty("billTypes")
          ? setBillTypesCount(counts.billTypes)
          : setBillTypesCount(false);
        counts.hasOwnProperty("chambers")
          ? setChambersCount(prev => {
              if (!deepEqual(prev, counts.chambers)) {
                return counts.chambers;
              }
              return prev;
            })
          : setChambersCount(false);
        counts.hasOwnProperty("party")
          ? setPartyCount(counts.party)
          : setPartyCount(false);
        counts.hasOwnProperty("deadlineCategories")
          ? setDealineCategoriesCount(counts.deadlineCategories)
          : setDealineCategoriesCount(false);
        counts.hasOwnProperty("sessionLawChapterNumbers")
          ? setChaptersCount(prev => {
              if (!deepEqual(prev, counts.sessionLawChapterNumbers)) {
                return counts.sessionLawChapterNumbers;
              }
              return prev;
            })
          : setChaptersCount(false);

        const tableRowData = formatBillStructureForDataTables(content);
        const formattedData = {
          columns: archiveTableHeading,
          rows: tableRowData,
        };

        if (formattedData.rows.length === 0) {
          setNoResultsMessage(`No results found`);
        } else {
          setNoResultsMessage(`Loading Bills...`);
        }

        setLoading(false);
        setBills(formattedData);
        setTotalPages(totalPages);
        setLastPage(last);
      } catch (error) {
        if (error.name !== "AbortError") {
          handleError(error);
        }
      } finally {
        setLoading(false);
        setAbortControllerQueue((prevQueue) =>
          prevQueue.filter((controller) => controller !== abortController)
        );
      }
    },
    [
      offset,
      sessionId,
      selectedBillTypes,
      selectedLegislator,
      selectedDrafter,
      selectedParties,
      selectedChambers,
      subjectCode,
      selectedCommittee,
      selectedProgress,
      hasAmendment,
      hasFiscalNotes,
      hasLegalNotes,
      selectedDeadlines,
      selectedActionTypes,
      billSearchText,
      statusStartDate,
      statusEndDate,
      selectedChapters,
    ]
  );

  const searchLawsArchiveAndSetExportBills = async () => {
    try {
      const {
        data: {
          bills: { content },
        },
      } = await axios.post(
        `${process.env.REACT_APP_LAWS_ARCHIVE_BACKEND_HOST}/v1/bills/search?limit=9999&offset=0`,
        filterValidValues({
          sessionId: sessionId,
          billTypes: selectedBillTypes,
          legislator: selectedLegislator,
          subjects: subjectCode,
          drafter: selectedDrafter,
          parties: selectedParties,
          chamber:
            selectedChambers.includes("S") && selectedChambers.includes("H")
              ? ""
              : selectedChambers[0],
          committeeCode: selectedCommittee,
          currentProgressCategory:
            selectedProgress && selectedProgress !== 0
              ? String(selectedProgress)
              : undefined,
          currentStatus: selectedActionTypes,
          hasAmendment: hasAmendment,
          hasFiscalNote: hasFiscalNotes,
          hasLegalNote: hasLegalNotes,
          deadlineCategories: selectedDeadlines,
          text: billSearchText,
          sortBy: "billNumber",
          dateFrom: statusStartDate,
          dateTo: statusEndDate,
        })
      );
      const exportData = formatBillStructureForDataExport(content);
      setDataForExport(exportData);
      setExportDataReady(true);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled");
      } else {
        console.error("Error fetching data: ", error);
        throw error;
      }
    }
  };

  const loadExportData = async ({ formatToExport }) => {
    try {
      if (sessionId < 1000) {
        await searchLawsAndSetExportBills({ formatToExport });
      } else {
        await searchLawsArchiveAndSetExportBills();
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (sessionId < 1000) {
        searchLawsAndSetBills(100).catch((error) => console.error(error));
      } else {
        searchLawsArchiveAndSetBills(100).catch((error) =>
          console.error(error)
        );
      }
    }, 500);
    setLoading(true);
    return () => clearTimeout(0);
  }, [
    offset,
    sessionId,
    selectedBillTypes,
    selectedLegislator,
    selectedDrafter,
    selectedParties,
    selectedChambers,
    subjectCode,
    selectedCommittee,
    selectedProgress,
    hasAmendment,
    hasFiscalNotes,
    hasLegalNotes,
    selectedDeadlines,
    billSearchText,
    statusStartDate,
    statusEndDate,
    selectedActionTypes,
    selectedChapters,
    hasPreIntroductionRequired,
    requestor,
    requestorType,
    byRequestOf,
    byRequestOfType,
    tableHeading,
  ]);

  // Second useEffect hook: Handles logic when sessionId >= 1000  *** IMP: REMOVE AFTER LAWS API GETS IT'S SEARCH FUNCTIONALITY ***
  useEffect(() => {
    if (sessionId >= 1000) {
      const delaySearch = setTimeout(() => {
        searchLawsArchiveAndSetBills(100).catch((error) =>
          console.error(error)
        );
      }, 500);

      setLoading(true);
      return () => clearTimeout(delaySearch);
    }
  }, [
    sessionId,
    offset,
    selectedBillTypes,
    selectedLegislator,
    selectedDrafter,
    selectedParties,
    selectedChambers,
    subjectCode,
    selectedCommittee,
    selectedProgress,
    hasAmendment,
    hasFiscalNotes,
    hasLegalNotes,
    selectedDeadlines,
    billSearchText,
    statusStartDate,
    statusEndDate,
    selectedActionTypes,
    selectedChapters,
    hasPreIntroductionRequired,
  ]);

  const handleColumnSelect = (value) => {
    let updatedSelection;
    if (selectedHeaderValue.includes(value)) {
      updatedSelection = selectedHeaderValue.filter((item) => item !== value);
    } else {
      updatedSelection = [...selectedHeaderValue, value];
    }
    setSelectedHeaderValue(updatedSelection);

    // Set the cookie with the updated selection
    setCookie("selectedColumns", JSON.stringify(updatedSelection), {
      path: "/",
      maxAge: 604800,
    });
  };

  const setSelectedHeaders = (selectedValueArray) => {
    setSelectedHeaderValue(selectedValueArray);
    const updatedSelectedHeadersUsingUserSelectedValue = tableHeading.map(
      (tableHeader) => {
        if (
          selectedValueArray.includes(tableHeader.field) &&
          tableHeader.dynamicHeader
        )
          tableHeader["displayHeader"] = true;
        if (
          !selectedValueArray.includes(tableHeader.field) &&
          tableHeader.dynamicHeader
        )
          tableHeader["displayHeader"] = false;
        return tableHeader;
      }
    );
    setTableHeading(updatedSelectedHeadersUsingUserSelectedValue);
  };

  // Helper for deep equality (shallow object comparison for chaptersCount)
  function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== typeof obj2) return false;
    if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) return false;
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (let key of keys1) {
      if (obj1[key] !== obj2[key]) return false;
    }
    return true;
  }

  return (
    <div>
      <Header
        setSideNavToggle={setSideNavToggle}
        sideNavToggle={sideNavToggle}
        dataForExport={dataForExport}
        setBillSearchText={setBillSearchText}
        loadExportData={loadExportData}
        exportDataReady={exportDataReady}
        setExportDataReady={setExportDataReady}
        setOffset={setOffset}
        sessionInfoForHeader={sessionInfoForHeader}
      />
      <MDBRow>
        <MDBCol>
          {sessionId < 1000 ? (
            <MDBDropdown title="Column Toggle" className="mb-2">
              <MDBDropdownToggle>
                <MDBIcon fas icon="columns" />
              </MDBDropdownToggle>
              <MDBDropdownMenu>
                {selectHeaders.map((header) => (
                  <MDBDropdownItem
                    key={header.value}
                    link
                    onClick={(e) => {
                      if (
                        e.target.tagName !== "INPUT" &&
                        e.target.tagName !== "LABEL"
                      ) {
                        handleColumnSelect(header.value);
                      }
                    }}
                    active={selectedHeaderValue.includes(header.value)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center w-100">
                      <MDBCheckbox
                        id={header.value}
                        label={header.text}
                        value={header.value}
                        checked={selectedHeaderValue.includes(header.value)}
                        onChange={() => handleColumnSelect(header.value)}
                      />
                    </div>
                  </MDBDropdownItem>
                ))}
              </MDBDropdownMenu>
            </MDBDropdown>
          ) : (
            <></>
          )}
        </MDBCol>
        <MDBCol>
          <Pagination
            offsetPage={offset}
            setOffsetPage={setOffset}
            totalPages={totalPages}
            lastPage={lastPage}
          />
        </MDBCol>
      </MDBRow>
      <MDBDatatable
        data={bills}
        className="bills-datatable"
        hover
        style={{ overflow: "auto" }}
        entries={1000}
        fixedHeader
        pagination={false}
        isLoading={loading}
        striped
        bordered
        noFoundMessage={noResultsMessage}
        //Nothing matches the search criteria.
        loadingMessage="Loading Bills..."
        sortField="Bill #"
        sortdesc="asc"
        maxHeight="100vh"
      />
      <MDBFooter className="d-flex justify-content-between align-items-center mt-2 pb-2">
        {totalElements ? (
          <small className="small">{totalElements} Total Bills</small>
        ) : (
          <></>
        )}
        <Pagination
          offsetPage={offset}
          setOffsetPage={setOffset}
          totalPages={totalPages}
          lastPage={lastPage}
        />
      </MDBFooter>
    </div>
  );
};

export default BillsTable;
